import React, { useState, useEffect } from 'react';
import { getApiClient } from 'utils/apiClient';
import { BesperPlatformSlatMark, isBesperVendorProviderId } from 'besper-product-marks';
import { AuthenticatedDatasetImage } from './AuthenticatedDatasetImage';
import { ProductSVGIcon } from './ProductSVGIcon';

/**
 * Product Info Modal - Shows extended product details
 * Fetches extended description from the backend on demand.
 * Layout: product logo left, provider right; title; description (extended or fallback); Continue button.
 */
export const ProductInfoModal = ({ product, onClose, onSelect, onOpenProvider }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [detailsLoadFailed, setDetailsLoadFailed] = useState(false);

  const marketplace = product?.marketplace || {};
  const productName = product?.name || 'Unnamed Product';
  const providerDisplayName = marketplace.providerDisplayName ||
    marketplace.provider?.displayName ||
    marketplace.providerId ||
    'Unknown Provider';

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setDetailsLoadFailed(false);
        const apiClient = getApiClient();
        const response = await apiClient.get(
          `/v1/payments/products/${encodeURIComponent(product.id)}/details`
        );
        if (response && response.success) {
          setDetails(response);
        } else {
          setDetailsLoadFailed(true);
        }
      } catch (err) {
        console.error('Error fetching product details:', err);
        setDetailsLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    if (product?.id) {
      fetchDetails();
    }
  }, [product?.id]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const productLogoUrl = marketplace.productLogoUrl || product?.metadata?.product_logo_url;
  const providerLogoUrl = marketplace.providerLogoUrl || marketplace.provider?.logo_url;
  const descriptionText = (details?.extended_description || details?.description || marketplace.shortDescription || product?.description || '').trim() || 'No description available.';
  const providerId = marketplace.providerId || marketplace.provider?.id;
  const stripeMeta = product?.metadata || {};
  const isDatasetProduct = String(stripeMeta.resource_type || '').toLowerCase() === 'dataset';
  const providerIdForMark = providerId || stripeMeta.provider;

  const handleBuy = () => {
    if (onSelect) onSelect(product);
    onClose();
  };

  return (
    <div className="product-info-modal-backdrop" onClick={handleBackdropClick}>
      <div className="product-info-modal">
        <button className="product-info-modal-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {loading ? (
          <div className="product-info-modal-loading">
            <div className="product-info-spinner" />
            <span>Loading product details...</span>
          </div>
        ) : (
          <div className="product-info-modal-content">
            {/* Header row: product logo left, provider right */}
            <div className="product-info-modal-header">
              <div className="product-info-logo-section">
                {productLogoUrl && !isDatasetProduct ? (
                  <AuthenticatedDatasetImage
                    logoUrl={productLogoUrl}
                    alt={productName}
                    className="product-info-logo"
                  />
                ) : (
                  <ProductSVGIcon
                    productName={productName}
                    productId={product.id}
                    productType={stripeMeta.resource_type}
                    productKey={stripeMeta.product_key}
                    datasetDestination={marketplace.defaultDatasetDestination || stripeMeta.default_dataset_destination}
                    size={96}
                  />
                )}
              </div>
              <div className="product-info-provider">
                {isBesperVendorProviderId(providerIdForMark) ? (
                  <BesperPlatformSlatMark
                    size={40}
                    className="product-info-provider-logo besper-vendor-platform-mark"
                    title={providerDisplayName}
                  />
                ) : providerLogoUrl ? (
                  <AuthenticatedDatasetImage
                    logoUrl={providerLogoUrl}
                    alt=""
                    className="product-info-provider-logo"
                  />
                ) : (
                  <div className="product-info-provider-badge">
                    <span>{providerDisplayName.charAt(0).toUpperCase()}</span>
                  </div>
                )}
              </div>
            </div>

            <h2 className="product-info-title">{productName}</h2>

            <div className="product-info-description-block">
              <p className="product-info-description">{descriptionText}</p>
            </div>

            <div className="product-info-provider-row">
              <span className="product-info-provider-label">Provider</span>
              {onOpenProvider && providerId ? (
                <button
                  type="button"
                  className="product-info-provider-link"
                  onClick={() => onOpenProvider({ providerId, providerDisplayName })}
                >
                  {providerDisplayName}
                </button>
              ) : (
                <span className="product-info-provider-value">{providerDisplayName}</span>
              )}
            </div>

            {detailsLoadFailed && !(details?.extended_description || details?.description) && (
              <p className="product-info-details-unavailable">Extended description is not available for this product.</p>
            )}

            {marketplace.features && marketplace.features.length > 0 && (
              <div className="product-info-features">
                <h3 className="product-info-features-label">Features</h3>
                <ul className="product-info-features-list">
                  {marketplace.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            {onSelect && (
              <div className="product-info-modal-actions">
                <button type="button" className="product-info-modal-buy-btn" onClick={handleBuy}>
                  Continue
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
